/**
 * useFollowers Hook
 * 
 * Manages follower data fetching and CRUD operations using React Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { followersAPI } from '@/services/api';
import { Follower, AddFollowerData, UpdateFollowerSettingsData, PatientFollowers } from '@/types/follower';

interface UseFollowersParams {
  patientId?: string | number;
  enabled?: boolean;
}

export const useFollowers = (params?: UseFollowersParams) => {
  const queryClient = useQueryClient();

  // Fetch followers
  const {
    data: followersData,
    isLoading,
    error,
    refetch,
  } = useQuery<PatientFollowers>({
    queryKey: ['followers', params?.patientId],
    queryFn: async () => {
      const response = await followersAPI.getAll({
        patientId: params?.patientId,
      });
      
      if (response.data?.success && response.data.data) {
        return response.data.data;
      } else if (response.data?.data) {
        return response.data.data;
      } else if (response.data) {
        return response.data;
      }
      
      return { patientId: params?.patientId || '', followers: [] };
    },
    enabled: params?.enabled !== false && !!params?.patientId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Add follower mutation
  const addMutation = useMutation({
    mutationFn: async ({ patientId, followerData }: { patientId: string | number; followerData: AddFollowerData }) => {
      const response = await followersAPI.create({
        patientId,
        ...followerData,
      });
      if (response.data?.success && response.data.data) {
        return response.data.data;
      } else if (response.data?.data) {
        return response.data.data;
      }
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['followers', variables.patientId] });
    },
  });

  // Update follower settings mutation
  const updateMutation = useMutation({
    mutationFn: async ({ 
      patientId, 
      followerId, 
      settings 
    }: { 
      patientId: string | number; 
      followerId: string; 
      settings: UpdateFollowerSettingsData 
    }) => {
      const response = await followersAPI.update(followerId, {
        patientId,
        ...settings,
      });
      if (response.data?.success && response.data.data) {
        return response.data.data;
      } else if (response.data?.data) {
        return response.data.data;
      }
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['followers', variables.patientId] });
    },
  });

  // Remove follower mutation
  const removeMutation = useMutation({
    mutationFn: async ({ patientId, followerId }: { patientId: string | number; followerId: string }) => {
      await followersAPI.delete(followerId);
      return followerId;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['followers', variables.patientId] });
    },
  });

  // Check if current user is following
  const isCurrentUserFollowing = (currentUserId?: string | number): boolean => {
    if (!followersData?.followers || !currentUserId) return false;
    return followersData.followers.some(
      f => f.type === 'user' && f.userId === currentUserId
    );
  };

  return {
    followers: followersData?.followers || [],
    isLoading,
    error,
    refetch,
    add: addMutation.mutate,
    update: updateMutation.mutate,
    remove: removeMutation.mutate,
    isAdding: addMutation.isPending,
    isUpdating: updateMutation.isPending,
    isRemoving: removeMutation.isPending,
    isCurrentUserFollowing,
  };
};


